import { RingProgress, Text, Tooltip } from "@mantine/core";
import React from "react";

interface CircularProgressIndicatorProps {
  value: number;
  maxValue?: number;
  size?: number;
  thickness?: number;
  borderWidth?: number;
  color?: string;
  borderColor?: string;
  showLabel?: boolean;
  hidePercentage?: boolean;
  /** Custom tooltip text. When omitted, shows "{value} out of {maxValue}" */
  tooltipLabel?: string;
}

const CircularProgressIndicator: React.FC<CircularProgressIndicatorProps> = ({
  value,
  maxValue = 100,
  size = 80,
  thickness = 10,
  color,
  borderColor,
  showLabel,
  hidePercentage,
  tooltipLabel,
}) => {
  const progressValue = (value * 100) / maxValue;
  const defaultTooltip =
    maxValue === 100
      ? `${Math.round(progressValue)}% filled`
      : `${value} out of ${maxValue}`;

  // MUI CircularProgress thickness is relative to its 44-unit viewBox;
  // RingProgress thickness is in px, so scale it to keep the same stroke.
  const strokeWidth = (thickness / 44) * size;

  return (
    <Tooltip label={tooltipLabel ?? defaultTooltip}>
      <RingProgress
        size={size}
        thickness={strokeWidth}
        sections={[
          {
            value: Math.min(progressValue, 100),
            color: color || "var(--mantine-primary-color-filled)",
          },
        ]}
        rootColor={borderColor || "var(--mantine-color-bg-disabled)"}
        label={
          showLabel ? (
            <Text
              size="xs"
              ta="center"
              component="div"
              c="var(--mantine-color-text-secondary)"
            >
              {`${progressValue}${hidePercentage ? "" : "%"}`}
            </Text>
          ) : undefined
        }
      />
    </Tooltip>
  );
};

export default CircularProgressIndicator;
